import React from 'react';
import { Link } from 'react-router-dom';
import { getAvatarUrl } from '../../utils/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHeart, FaRegHeart, FaComment, FaRegComment, FaRetweet,
  FaBookmark, FaRegBookmark, FaShare, FaPaperPlane, FaEllipsisH,
  FaTrophy, FaStar, FaRegStar, FaTrash, FaFlag, FaBan, FaEdit
} from 'react-icons/fa';
import VerificationBadge from '../VerificationBadge';
import AudioPlayer from '../AudioPlayer';
import LinkPreview from '../LinkPreview';
import MusicTag from '../MusicTag';
import TipButton from '../feed/TipButton';
import { toast } from 'sonner';

const formatTimeAgo = (dateString) => {
  const diff = Date.now() - new Date(dateString).getTime();
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
  return new Date(dateString).toLocaleDateString();
};

const parseContent = (content) => {
  if (!content) return content;
  return content.split(/(\s+)/).map((part, i) => {
    if (part.startsWith('#'))
      return <Link key={`hash-${i}-${part}`} to={`/hashtag/${part.slice(1)}`} className="text-[#4A90E2] hover:underline font-medium">{part}</Link>;
    if (part.startsWith('@'))
      return <Link key={`mention-${i}-${part}`} to={`/profile/${part.slice(1)}`} className="text-[#A855F7] hover:underline font-medium">{part}</Link>;
    return part;
  });
};

const POTDTrophyBadge = ({ size = 12 }) => (
  <span className="inline-flex items-center" title="Point of the Day Winner">
    <FaTrophy className="text-amber-500 animate-pulse" size={size} />
  </span>
);

const PostCard = ({ post, likedPosts, bookmarkedPosts, onLike, onBookmark, onRepost, onToggleComments, expandedComments, comments, newComment, onCommentChange, onAddComment, onNominate, nominatedPosts, currentUserId, followedUsers, onFollow, onDelete, onReport, onBlock, onEdit }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
  <motion.article
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-white rounded-2xl overflow-hidden hover:shadow-md transition-shadow ${
      post.is_potd_winner
        ? 'border-2 border-amber-300 ring-2 ring-amber-100 shadow-lg shadow-amber-100/50'
        : 'border border-gray-100'
    }`}
    data-testid={`post-${post.id}`}
  >
    {post.is_potd_winner && (
      <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 px-5 py-2 flex items-center space-x-2">
        <FaTrophy className="text-white" size={13} />
        <span className="text-xs font-bold text-white uppercase tracking-wider font-arthaus">Point of the Day</span>
        <span className="text-xs bg-white/30 text-white px-2 py-0.5 rounded-full font-bold">{post.potd_votes} votes</span>
      </div>
    )}
    {post.is_repost && (
      <div className="px-5 pt-3 flex items-center space-x-2 text-gray-400 text-xs">
        <FaRetweet size={11} />
        <span>{post.reposted_by_name} reposted</span>
      </div>
    )}
    <div className="p-5">
      <div className="flex items-start space-x-3 mb-3">
        <Link to={`/profile/${post.username}`} className="flex-shrink-0">
          <img src={getAvatarUrl(post.avatar_url, post.full_name)} alt={post.full_name} className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 hover:ring-[#4A90E2]/40 transition-all" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <Link to={`/profile/${post.username}`} className="hover:underline min-w-0">
                  <span className="text-sm font-bold text-gray-900 truncate block">{post.full_name}</span>
                </Link>
                {post.is_verified && <VerificationBadge badgeType={post.verification_badge} size="sm" />}
                {post.is_potd_winner && <POTDTrophyBadge size={11} />}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <span className="truncate">@{post.username}</span>
                <span className="flex-shrink-0">·</span>
                <span className="flex-shrink-0">{formatTimeAgo(post.created_at)}</span>
                {post.is_edited && <span className="text-gray-300 italic flex-shrink-0">(edited)</span>}
              </div>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
              {currentUserId && post.user_id !== currentUserId && (
                <button onClick={() => onFollow(post.user_id)} data-testid={`follow-btn-${post.id}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${followedUsers?.has(post.user_id) ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-gray-700'}`}>
                  {followedUsers?.has(post.user_id) ? 'Following' : 'Follow'}
                </button>
              )}
              <div className="relative" ref={menuRef}>
                <button onClick={() => setMenuOpen(!menuOpen)} className="text-gray-300 hover:text-gray-500 p-1 hover:bg-gray-50 rounded-full transition-colors" data-testid={`post-menu-${post.id}`}>
                  <FaEllipsisH size={13} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 min-w-[140px]">
                    {currentUserId && post.user_id === currentUserId ? (
                      <>
                        <button onClick={() => { setMenuOpen(false); onEdit(post); }} data-testid={`edit-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"><FaEdit size={12} /><span>Edit</span></button>
                        <button onClick={() => { setMenuOpen(false); onDelete(post.id); }} data-testid={`delete-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"><FaTrash size={12} /><span>Delete</span></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setMenuOpen(false); onReport(post.id, 'post'); }} data-testid={`report-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"><FaFlag size={12} /><span>Report</span></button>
                        <button onClick={() => { setMenuOpen(false); onBlock(post.user_id); }} data-testid={`block-user-${post.user_id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"><FaBan size={12} /><span>Block User</span></button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-gray-900 text-[15px] leading-relaxed mb-3 whitespace-pre-wrap">{parseContent(post.content)}</div>

      {post.media_url && (
        <div className="mb-3 rounded-xl overflow-hidden">
          {post.media_type === 'audio' ? <AudioPlayer audioUrl={post.media_url} />
            : post.media_type?.startsWith('image') ? <img src={post.media_url} alt="" loading="lazy" className="w-full object-cover max-h-[400px] rounded-xl" />
            : post.media_type?.startsWith('video') ? <video src={post.media_url} controls className="w-full max-h-[400px] rounded-xl" />
            : null}
        </div>
      )}

      {post.link_preview && <div className="mb-3"><LinkPreview linkData={post.link_preview} /></div>}

      {post.quoted_post && (
        <div className="border border-gray-100 rounded-xl p-4 mb-3 bg-gray-50/50">
          <div className="flex items-center space-x-2 mb-1.5">
            <Link to={`/profile/${post.quoted_post.username}`}><img src={getAvatarUrl(post.quoted_post.avatar_url, post.quoted_post.full_name)} alt="" className="w-5 h-5 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" /></Link>
            <Link to={`/profile/${post.quoted_post.username}`} className="hover:underline"><span className="font-semibold text-xs text-gray-700">{post.quoted_post.full_name}</span></Link>
            <span className="text-gray-400 text-xs">@{post.quoted_post.username}</span>
          </div>
          <p className="text-gray-600 text-sm line-clamp-2">{post.quoted_post.content}</p>
        </div>
      )}

      {post.music?.song_id && <MusicTag music={post.music} />}

      <div className="flex items-center justify-between pt-3 border-t border-gray-50 -mx-1">
        <button onClick={() => onLike(post.id)} data-testid={`like-btn-${post.id}`} className={`flex items-center space-x-1 px-2 py-1.5 rounded-full transition-all text-sm ${likedPosts.has(post.id) ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
          {likedPosts.has(post.id) ? <FaHeart size={14} /> : <FaRegHeart size={14} />}
          <span className="font-medium text-xs">{post.likes_count}</span>
        </button>
        <button onClick={() => onToggleComments(post.id)} data-testid={`comment-btn-${post.id}`} className={`flex items-center space-x-1 px-2 py-1.5 rounded-full transition-all text-sm ${expandedComments[post.id] ? 'text-[#4A90E2] bg-blue-50' : 'text-gray-400 hover:text-[#4A90E2] hover:bg-blue-50'}`}>
          {expandedComments[post.id] ? <FaComment size={14} /> : <FaRegComment size={14} />}
          <span className="font-medium text-xs">{post.comments_count}</span>
        </button>
        <button onClick={() => onRepost(post.id)} data-testid={`repost-btn-${post.id}`} className="flex items-center space-x-1 px-2 py-1.5 rounded-full text-gray-400 hover:text-green-500 hover:bg-green-50 transition-all text-sm">
          <FaRetweet size={14} /><span className="font-medium text-xs">{post.shares_count}</span>
        </button>
        <TipButton recipientId={post.user_id} targetType="post" targetId={post.id} currentUserId={currentUserId} />
        <button onClick={() => onBookmark(post.id)} data-testid={`bookmark-btn-${post.id}`} className={`flex items-center px-2 py-1.5 rounded-full transition-all text-sm ${bookmarkedPosts.has(post.id) ? 'text-yellow-500 bg-yellow-50' : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'}`}>
          {bookmarkedPosts.has(post.id) ? <FaBookmark size={14} /> : <FaRegBookmark size={14} />}
        </button>
        <button onClick={() => onNominate(post.id)} data-testid={`nominate-btn-${post.id}`} className={`flex items-center px-2 py-1.5 rounded-full transition-all text-sm ${nominatedPosts?.has(post.id) ? 'text-amber-500 bg-amber-50' : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'}`} title="Nominate for Point of the Day">
          {nominatedPosts?.has(post.id) ? <FaStar size={13} /> : <FaRegStar size={13} />}
        </button>
        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`); toast.success('Link copied!'); }} data-testid={`share-btn-${post.id}`} className="flex items-center px-2 py-1.5 rounded-full text-gray-400 hover:text-[#4A90E2] hover:bg-blue-50 transition-all text-sm">
          <FaShare size={13} />
        </button>
      </div>

      <AnimatePresence>
        {expandedComments[post.id] && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3 pt-3 border-t border-gray-50 space-y-3">
            <div className="flex space-x-2">
              <input type="text" value={newComment[post.id] || ''} onChange={(e) => onCommentChange(post.id, e.target.value)} onKeyPress={(e) => e.key === 'Enter' && onAddComment(post.id)} placeholder="Write a comment..." className="flex-1 bg-gray-50 rounded-full px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-200" data-testid={`comment-input-${post.id}`} />
              <button onClick={() => onAddComment(post.id)} className="bg-gray-900 text-white rounded-full px-3 py-2 transition-all hover:bg-gray-800" data-testid={`comment-send-${post.id}`}><FaPaperPlane size={12} /></button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {comments[post.id]?.map((comment) => (
                <div key={comment.id} className="flex space-x-2">
                  <Link to={`/profile/${comment.username}`}><img src={getAvatarUrl(comment.avatar_url, comment.full_name)} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" /></Link>
                  <div className="bg-gray-50 rounded-2xl px-3 py-2 flex-1">
                    <Link to={`/profile/${comment.username}`} className="hover:underline"><span className="font-semibold text-xs text-gray-900">{comment.full_name}</span></Link>
                    <p className="text-gray-700 text-sm">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </motion.article>
  );
};

export default PostCard;
